// Montaje del widget del buscador de renfeviajes: de las plantillas al DOM.
//
// Esto es sólo la fase 1: pinta el buscador y lo deja idéntico al de la home. No hay
// comportamiento — ni autocompletado, ni calendarios, ni pasajeros, ni salida. Eso es F2/F3.
//
// Lo que resuelve aquí y que en la home no hace falta resolver:
//   - los id. En la home hay 16 id duplicados dentro del propio buscador (#fechaEntrada sale
//     4 veces) y da igual porque sólo hay un buscador por página. Un widget puede montarse
//     dos veces en la misma landing, y la landing puede tener su propio #form: por eso el
//     markup no lleva un solo id y los que exige la accesibilidad se generan aquí, con el
//     número de instancia por delante.
//   - los name de los radios, por lo mismo: dos widgets en la misma página compartirían
//     grupo de radio y marcar "sólo ida" en uno desmarcaría el otro.
//   - los overlays (calendario, modales), que se cuelgan al final de <body> porque un
//     z-index no cruza el contexto de apilamiento de la landing.
import { plantillas, svgs } from './markup.js'
import { vigilarIconfont } from './iconfont.js'
import { textosPorDefecto, plurales } from './textos.js'

let contador = 0

// Los bloques que existen, en el orden en que van dentro de #form.
const BLOQUES = ['bloquePaqueteTren', 'bloqueTrenHotelActividad', 'bloqueHotel', 'bloqueActividades']

// Las pestañas, en el orden de la barra, con el bloque que abre cada una. Hotel y coche no
// abren bloque: saltan fuera (Booking y la página de coches).
export const PESTANAS = [
  { clave: 'paquetetren', marca: 'tab_paquetetren', bloque: 'bloquePaqueteTren' },
  { clave: 'trenhotelactividad', marca: 'tab_trenhotelactividad', bloque: 'bloqueTrenHotelActividad' },
  { clave: 'hotel', marca: 'tab_hotel', bloque: null },
  { clave: 'coche_alquiler', marca: 'tab_coche_alquiler', bloque: null },
  { clave: 'actividades', marca: 'tab_actividades', bloque: 'bloqueActividades' },
]

// Los colores que una landing puede cambiar, y la variable CSS de cada uno. Los nombres son
// del PAPEL que cumple el color, no del color que es hoy: `data-color-marca` seguirá
// queriendo decir lo mismo el día que la marca deje de ser rosa.
export const COLORES = {
  marca: '--rvb-rosa',                // pestaña activa, iconos, el botón de buscar
  boton: '--rvb-morado',              // el botón principal y el hover
  // El de BUSCAR, aparte: una landing puede quererlo en un tono claro con el texto oscuro, y
  // ese mismo color en los botones de los modales —que llevan texto blanco— no se leería.
  'boton-buscar': '--rvb-boton-buscar-fondo',
  'boton-buscar-texto': '--rvb-boton-buscar-texto',
  'boton-buscar-hover': '--rvb-boton-buscar-hover',
  texto: '--rvb-gris-texto',          // los rótulos
  'texto-suave': '--rvb-gris-medio',  // las ayudas y lo secundario
  borde: '--rvb-gris-borde',          // el borde de los campos
  separador: '--rvb-gris-claro',      // las líneas entre filas
  foco: '--rvb-azul',                 // el campo activo
  error: '--rvb-rojo-error',          // el campo en error
}

// Las MEDIDAS que puede ajustar una landing, y la variable de cada una. Son las que el skin de
// Corporate Benefits tenía que reescribir a mano: esquinas, alto de campo, borde, relleno,
// tamaños de letra y el hueco entre columnas.
export const MEDIDAS = {
  fuente: '--rvb-fuente',                  // la tipografía de todo el widget
  'radio-campo': '--rvb-radio-campo',      // las esquinas del recuadro de los campos
  'radio-campo-movil': '--rvb-radio-campo-movil',
  'radio-caja': '--rvb-radio-caja',        // las de los desplegables y los modales
  'radio-boton': '--rvb-radio-boton',      // las de los botones de accion: Sí/No, Listo, Aceptar
  'alto-campo': '--rvb-alto-campo',
  'borde-campo': '--rvb-borde-campo',      // el borde entero: `1px solid #9ca6a7`
  'fondo-campo': '--rvb-fondo-campo',
  'relleno-campo': '--rvb-relleno-campo',
  'letra-campo': '--rvb-letra-campo',
  'letra-rotulo': '--rvb-letra-rotulo',
  hueco: '--rvb-hueco',                    // entre las dos columnas y entre filas
  'sombra-caja': '--rvb-sombra-caja',
}

// Una medida puede ser casi cualquier cosa (`8px 24px`, `1px solid #ccc`, `clamp(...)`), así
// que en vez de describir lo que vale se prohíbe lo que hace daño: nada de `url(` —que traería
// una petición a donde diga la landing—, ni caracteres que permitan salirse de la declaración.
const MEDIDA_PELIGROSA = /url\(|expression|[;{}<>]/i

// Lo que se acepta como color. Esto viene de la página de un tercero y acaba dentro de un
// `style`, así que no vale cualquier cosa: sólo un hexadecimal, un rgb()/rgba() de números, o
// un nombre de color CSS. Lo que no case, se ignora en silencio salvo aviso por consola.
const COLOR_VALIDO = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|[a-z]{3,20})$/i

/**
 * Pinta el widget con los colores que pida la landing.
 *
 * Se aplican a la raíz Y al contenedor de overlays, que es otro elemento y cuelga del <body>:
 * si no, el calendario y los modales se quedarían con los colores de renfeviajes mientras el
 * buscador va con los de la landing.
 */
export function aplicarColores (elementos, colores) {
  if (!colores) return
  for (const [papel, valor] of Object.entries(colores)) {
    const variable = COLORES[papel]
    if (!variable || !valor) continue
    if (!COLOR_VALIDO.test(String(valor).trim())) {
      console.warn('[buscador] color no válido para ' + papel + ': ' + valor)
      continue
    }
    elementos.filter(Boolean).forEach((el) => el.style.setProperty(variable, String(valor).trim()))
  }
}

/**
 * Las medidas que pida la landing, en la raíz y en los overlays (los desplegables y los
 * modales cuelgan del <body>, así que necesitan su propia copia).
 */
export function aplicarMedidas (elementos, medidas) {
  if (!medidas) return
  for (const [nombre, valor] of Object.entries(medidas)) {
    const variable = MEDIDAS[nombre]
    if (!variable || !valor) continue
    const limpio = String(valor).trim()
    if (!limpio || limpio.length > 120 || MEDIDA_PELIGROSA.test(limpio)) {
      console.warn('[buscador] medida no válida para ' + nombre + ': ' + valor)
      continue
    }
    elementos.filter(Boolean).forEach((el) => el.style.setProperty(variable, limpio))
  }
}

// Los nombres con los que una landing puede pedir cada pestaña. A la izquierda, lo que se
// escribe en `data-pestanas`; a la derecha, la clave de dentro. Se admiten los dos.
const ALIAS_PESTANAS = {
  tren: 'paquetetren',
  trenhotel: 'paquetetren',
  trenmashotel: 'paquetetren',
  'tren+hotel': 'paquetetren',
  paquetetren: 'paquetetren',
  trenhotelactividad: 'trenhotelactividad',
  trenhotelocio: 'trenhotelactividad',
  hotel: 'hotel',
  hoteles: 'hotel',
  coche: 'coche_alquiler',
  coches: 'coche_alquiler',
  coche_alquiler: 'coche_alquiler',
  ocio: 'actividades',
  actividades: 'actividades',
}

/**
 * Traduce lo que pide la landing a claves de pestaña, quitando lo que no exista.
 * @param {string|Array} pedidas
 * @returns {Array<string>} las claves, en el orden en que están en la barra
 */
export function pestanasPedidas (pedidas) {
  const lista = (Array.isArray(pedidas) ? pedidas : String(pedidas || '').split(','))
    .map((x) => String(x).trim().toLowerCase().replace(/\s+/g, ''))
    .filter(Boolean)
    .map((x) => ALIAS_PESTANAS[x])
    .filter(Boolean)
  if (!lista.length) return []
  // Se respeta el ORDEN DE LA BARRA, no el que venga en el atributo: el CSS del buscador
  // cuelga de la posición (`:first-child`, `:last-child`) y reordenarlas lo rompe.
  return PESTANAS.map((p) => p.clave).filter((clave) => lista.includes(clave))
}

/**
 * Esconde la barra de pestañas entera. Para una landing de una sola cosa —sólo tren + hotel,
 * por ejemplo— donde el selector no pinta nada: se esconde el contenedor, no las pestañas una
 * a una, para que no quede la franja vacía con su relleno.
 *
 * Las pestañas siguen ahí y cableadas: `data-tab` o la API pueden cambiar de bloque aunque no
 * se vea la barra.
 */
export function ocultarBarraDePestanas (raiz) {
  const barra = raiz.querySelector('.rvb-topbar-container')
  if (barra) barra.classList.add('rvb-oculto')
}

/**
 * Deja a la vista sólo las pestañas pedidas. Si no se reconoce ninguna, no toca nada: más vale
 * el buscador entero que uno sin pestañas.
 * @returns {Array<string>} las que han quedado visibles
 */
export function limitarPestanas (raiz, pedidas) {
  const claves = pestanasPedidas(pedidas)
  if (!claves.length) return PESTANAS.map((p) => p.clave)
  PESTANAS.forEach((p) => {
    const boton = raiz.querySelector('[data-rvb="' + p.marca + '"]')
    if (boton) boton.classList.toggle('rvb-oculto', !claves.includes(p.clave))
  })
  return claves
}

/**
 * Sustituye los marcadores que dejó el generador.
 * @param {string} html
 * @param {object} textos  id de traducción -> texto ya en el idioma que toca
 */
function resolver (html, textos) {
  return html
    .replace(/\{\{svg:([A-Za-z0-9]+)\}\}/g, (_, nombre) => svgs[nombre] || '')
    .replace(/\{\{(\d+)\}\}/g, (_, id) => {
      const t = textos[id]
      // Un texto que falte no puede tumbar la landing de un tercero: se cae al castellano
      // de la plantilla. Es justo lo contrario de lo que hace {tr} en el proyecto, que
      // lanza una excepción y deja la página a medias.
      if (t === undefined) return escapar(textosPorDefecto[id] !== undefined ? textosPorDefecto[id] : '')
      return escapar(t)
    })
}

function escapar (texto) {
  return String(texto).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Convierte un trozo de plantilla en nodos.
 */
function nodos (html, textos) {
  const caja = document.createElement('div')
  caja.innerHTML = resolver(html, textos)
  return caja.firstElementChild
}

/**
 * Los id y los name que hacen falta para que el widget sea accesible, prefijados con el
 * número de instancia.
 */
function identificar (raiz, prefijo) {
  raiz.querySelectorAll('[data-rvb-name]').forEach((el) => {
    el.setAttribute('name', prefijo + '-' + el.getAttribute('data-rvb-name'))
  })

  raiz.querySelectorAll('[data-rvb-for]').forEach((etiqueta) => {
    const quiere = etiqueta.getAttribute('data-rvb-for')
    // El markup actual asocia por contención (el <label> envuelve al campo) y además lleva
    // un for=; en varios casos ese for apunta a un id que no existe — `for="estacionOrigen"`
    // sobre un campo que se llama `estacionOrigenTrenActividad`. Cuando el destino no
    // aparece, el for se queda fuera: la contención ya asocia, y un for roto es peor que
    // ninguno porque el lector de pantalla anuncia una etiqueta vacía.
    let destino = etiqueta.querySelector('[data-rvb="' + quiere + '"]')
    if (!destino) {
      const bloque = etiqueta.closest('[data-rvb^="bloque"]') || raiz
      destino = bloque.querySelector('[data-rvb="' + quiere + '"]')
    }
    if (!destino) {
      // NO BASTA CON QUE EL LABEL ENVUELVA AL CAMPO, y esto costó un cliente enfadado.
      //
      // Un <label> sin `for` se asocia al PRIMER descendiente asociable, y aquí el primero no
      // es el campo: es el botón de volver del panel de móvil, que en el markup va delante.
      //
      //     <label data-rvb-for="estacionDestino">
      //       <button class="rvb-icon-back">   <-- el navegador asocia el label A ESTO
      //       <span>Hasta</span>
      //       <input class="rvb-station2Input">
      //
      // Resultado en un móvil: tocar la píldora fuera del hueco exacto del campo PULSA LA
      // FLECHA en vez de enfocar, y el cliente tiene que insistir hasta acertar dentro del
      // input --"he tenido que dar 5 veces en destino para que me dejara"--. Medido en el
      // iPhone del cliente: `mousedown -> label`, `click -> label`, `click -> icon-back`, y el
      // foco se queda en el body.
      //
      // Así que cuando el `for` del markup apunta a un id que no existe --que es lo normal
      // aquí: dice `estacionDestino` y el campo se llama `estacionDestinoTrenActividad`-- se
      // apunta al primer campo DE ESCRITURA, sin contar botones.
      const campo = etiqueta.querySelector('input, select, textarea')
      if (!campo) {
        console.warn('[buscador] label sin destino ni campo dentro: ' + quiere)
        return
      }
      if (!campo.id) campo.id = prefijo + '-' + quiere + '-' + (++contador)
      etiqueta.setAttribute('for', campo.id)
      return
    }
    if (!destino.id) destino.id = prefijo + '-' + quiere + '-' + (++contador)
    etiqueta.setAttribute('for', destino.id)
  })
}

/**
 * Monta el buscador dentro de un elemento.
 *
 * @param {Element|string} donde   el nodo o un selector
 * @param {object} opciones
 * @param {object} [opciones.textos]   id de traducción -> texto; lo que falte cae al castellano
 * @param {string} [opciones.pestana]  pestaña abierta al montar
 * @returns {{raiz: Element, overlays: Element, plantilla: Function, destruir: Function}|null}
 */
export function montar (donde, opciones = {}) {
  const destino = typeof donde === 'string' ? document.querySelector(donde) : donde
  if (!destino) { console.warn('[buscador] no encuentro dónde montar: ' + donde); return null }
  if (destino.querySelector('.rvb-w')) { console.warn('[buscador] ya montado aquí'); return null }

  const textos = opciones.textos || {}
  const prefijo = 'rvb' + (++contador)

  // --- la envoltura, con las pestañas y los bloques dentro
  const bloquesHtml = BLOQUES.map((b) => plantillas[b]).join('\n')
  const envoltura = nodos(
    plantillas.envoltura
      .replace('{{pestanas}}', plantillas.pestanas)
      .replace('{{bloques}}', bloquesHtml),
    textos)

  // La clase que acota el CSS va en un contenedor POR ENCIMA de todo, no en el primer
  // elemento del buscador: las reglas generadas son de la forma `.rvb-w <lo que sea>`, así
  // que el ámbito tiene que ser un antepasado de todo, incluido el contenedor exterior.
  const raiz = document.createElement('div')
  raiz.className = 'rvb-w'
  raiz.setAttribute('data-rvb-instancia', prefijo)
  // El interruptor de la portada. Va en el ámbito y no en el hueco porque las reglas se
  // cuelgan de `.rvb-w`, y así el CSS puede decir "todo esto, menos en la portada".
  if (opciones.portada) {
    raiz.setAttribute('data-portada', '')
    // Y el HUECO recupera el id `buscador_container`, que es el que tenía ese sitio en la
    // portada. No es nostalgia: de ese id cuelgan DIEZ reglas del agente con el combinador
    // hermano —`#buscador_container ~ #headerCustomCode`— y no sólo colocan el buscador; le
    // cambian a la cabecera el alto, el relleno, cuál de los dos logos se ve y dónde caen los
    // desplegables de idioma, ocio y ciudad. Sin el id, la cabecera entera de renfeviajes sale
    // distinta, que es lo que se vio en producción.
    //
    // Es la excepción a la regla de no usar id (ver la trampa de los id duplicados): aquí hay
    // un solo buscador, es la home, y el id ya estaba ahí. En una landing no se pone, porque
    // `data-portada` sólo lo pide el PHP de la portada.
    if (!document.getElementById('buscador_container')) destino.id = 'buscador_container'
  }
  raiz.appendChild(envoltura)

  // El contenedor del buscador nace oculto en la home porque lo enseña el JS cuando termina
  // de prepararlo; aquí se monta ya visible.
  const caja = raiz.querySelector('[data-rvb="buscadorDestinosRenfe"]')
  if (caja) caja.classList.remove('rvb-oculto')

  // Los campos ocultos de la Search Extension de Booking. Aquí sólo se pintan; quien los
  // rellena y quien carga el script de bstatic es F3.
  const booking = nodos(plantillas.bookingCampos, textos)
  if (booking) raiz.appendChild(booking)

  // El velo de "cargando" NO se monta aquí. Se crea cuando hace falta (buscador.cargando()).
  //
  // Por qué: es un elemento a pantalla completa, `position: fixed`, y lo único que lo esconde
  // es una clase del CSS del widget. Si esa hoja tarda en llegar —o no llega— el velo tapa la
  // landing entera y no se puede pulsar nada. En la plantilla de la home eso no pasa porque
  // lleva un `style="display:none"` inline, que no depende de ninguna hoja; aquí los estilos
  // inline se han quitado a propósito por la CSP, así que la forma segura es no tenerlo
  // puesto mientras no se use.

  identificar(raiz, prefijo)

  // --- los overlays, al final de <body>
  // Un z-index sólo compite dentro de su contexto de apilamiento: si la landing mete el
  // widget dentro de un elemento con transform, opacity o filter, el calendario se quedaría
  // atrapado dentro por mucho z-index que llevara.
  const overlays = document.createElement('div')
  overlays.className = 'rvb-w rvb-overlays'
  overlays.setAttribute('data-rvb-instancia', prefijo)

  // TODO LO QUE CAMBIA EL ASPECTO, ANTES DE QUE LOS NODOS ENTREN EN EL DOCUMENTO.
  //
  // No es manía de orden: en cuanto un elemento con `rvb-icon-*` está en el documento, el
  // navegador ve el `font-family: icomoon` que le pone la capa del agente y pide la fuente —y
  // fuera de renfeviajes esa petición la bloquea CORS y deja dos errores en la consola de la
  // landing, aunque el icono acabe pintándose con su contorno—. Marcando antes, no llega a
  // pedirla. Y de paso, los colores y las medidas se aplican sin que se vea el cambio.
  if (opciones.maqueta) {
    raiz.setAttribute('data-maqueta', opciones.maqueta)
    overlays.setAttribute('data-maqueta', opciones.maqueta)
  }
  aplicarColores([raiz, overlays], opciones.colores)
  aplicarMedidas([raiz, overlays], opciones.medidas)
  vigilarIconfont([raiz, overlays])

  destino.appendChild(raiz)
  document.body.appendChild(overlays)

  const pedida = opciones.pestana && PESTANAS.find((p) => p.clave === opciones.pestana)
  if (pedida) seleccionarPestana(raiz, pedida.clave)

  return {
    raiz,
    overlays,
    prefijo,
    plurales,
    // Para que F2 pueda pedir las plantillas de los overlays (pasajeros, calendario de
    // hotel, habitación) resueltas con los mismos textos.
    plantilla (nombre) {
      if (!plantillas[nombre]) { console.warn('[buscador] no existe la plantilla ' + nombre); return null }
      const el = nodos(plantillas[nombre], textos)
      identificar(el, prefijo)
      return el
    },
    destruir () {
      raiz.remove()
      overlays.remove()
    },
  }
}

/**
 * Abre una pestaña: marca la de la barra y enseña su bloque.
 */
export function seleccionarPestana (raiz, clave) {
  const pestana = PESTANAS.find((p) => p.clave === clave)
  if (!pestana) return
  PESTANAS.forEach((p) => {
    const t = raiz.querySelector('[data-rvb="' + p.marca + '"]')
    if (t) t.classList.toggle('rvb-tab_selected', p.clave === clave)
  })
  BLOQUES.forEach((b) => {
    const el = raiz.querySelector('[data-rvb="' + b + '"]')
    if (el) el.classList.toggle('rvb-oculto', b !== pestana.bloque)
  })
}
